// posthog.ts — browser-side init for PostHog session replay + autocapture +
// exception capture + console/network capture (all four in one SDK).
//
// Mirrors main's posthog.ts: lazy import so the module is import-safe under
// SSR / unit tests without dragging posthog-js in, and the SDK is never
// touched when PUBLIC_POSTHOG_KEY is empty (no-op everywhere).
//
// Identity flow on this branch (capability-marketplace):
//   hooks.client.ts boots → initPostHog()
//   +layout.svelte's initAuth() $effect resolves → identifyUser(profile().sub, …)
//   sign-out / auth error → resetUser()

import { env } from '$env/dynamic/public';

const e: Record<string, string | undefined> =
	(env as Record<string, string | undefined> | undefined) ?? {};
const PUBLIC_POSTHOG_KEY = e.PUBLIC_POSTHOG_KEY ?? '';
const PUBLIC_POSTHOG_HOST = e.PUBLIC_POSTHOG_HOST ?? 'https://eu.i.posthog.com';
// Numeric PostHog project id, used ONLY to build replay deep-links for the admin
// Flagged tab. Optional: when unset the tab shows the replay id as copyable text
// instead of a link (honest fallback). Not a secret — a public project identifier.
const PUBLIC_POSTHOG_PROJECT_ID = e.PUBLIC_POSTHOG_PROJECT_ID ?? '';
const PUBLIC_SAYANO_AI_ENV = e.PUBLIC_SAYANO_AI_ENV ?? '';
const PUBLIC_SAYANO_AI_RELEASE = e.PUBLIC_SAYANO_AI_RELEASE ?? '';

let initialised = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let posthog: any = null;

export async function initPostHog(): Promise<boolean> {
	if (initialised) return true;
	if (!PUBLIC_POSTHOG_KEY) return false;
	if (typeof window === 'undefined') return false;

	const mod = await import('posthog-js');
	posthog = mod.default ?? mod;
	posthog.init(PUBLIC_POSTHOG_KEY, {
		api_host: PUBLIC_POSTHOG_HOST,
		// `defaults: '2026-01-30'` opts into PostHog's modern recommended
		// defaults: session replay on, autocapture on, exception capture on,
		// network + console + web-vitals capture on, person_profiles set to
		// 'identified_only' (no profile burn on anonymous traffic). When PostHog
		// rolls a newer dated default, we bump this constant deliberately
		// rather than silently inheriting changes.
		defaults: '2026-01-30',
		// Override: pageviews are emitted manually from +layout.svelte's
		// afterNavigate so SvelteKit's client-side router transitions don't
		// double-count against PostHog's history-API listener.
		capture_pageview: false,
		// Override the defaults' maskAllInputs:true — replays are an internal
		// debugging tool viewed only by project members, and seeing what the
		// user actually typed (chat composer, marketplace forms) is the point.
		// Deliberate decision 2026-06-12. Passwords stay masked unconditionally:
		// the only password-shaped input in this app is nothing today, but
		// keeping the guard costs us nothing and protects future forms.
		session_recording: {
			maskAllInputs: false,
			maskInputOptions: { password: true },
			// Accept replay events posted up from the sandboxed report/artifact
			// iframes (opaque origin → unreadable from here). The matching child
			// recorder is injected at blob-build time — see replay-iframe.ts.
			recordCrossOriginIframes: true,
		},
	});
	// `register` attaches super-properties to every event going forward.
	// Tagging env + release lets the PostHog UI scope dashboards to staging
	// vs prod (parallels Sentry/GlitchTip). NOTE: bootstrap.sessionProperties
	// looks like it should work but isn't a real BootstrapConfig field — it's
	// silently dropped. register() is the supported path.
	posthog.register({
		sayano_env: PUBLIC_SAYANO_AI_ENV || 'development',
		sayano_release: PUBLIC_SAYANO_AI_RELEASE || undefined,
	});
	initialised = true;
	return true;
}

export function isPostHogOn(): boolean {
	return initialised;
}

/** Link the current browser session to a known user. Idempotent for a given
 *  distinctId — PostHog merges anonymous events captured before identify with
 *  the identified profile. Safe to call once auth has resolved.
 *
 *  Properties shape is intentionally loose so this module stays decoupled
 *  from the auth/me types. */
export function identifyUser(
	distinctId: string,
	properties?: Record<string, unknown>,
): void {
	if (!posthog || !distinctId) return;
	posthog.identify(distinctId, properties);
}

/** Drop the link to the identified user. Use on sign-out / auth-cleared so
 *  the next user on the same browser doesn't inherit the previous identity.
 *  Session replays after reset are recorded against a fresh anonymous id. */
export function resetUser(): void {
	if (!posthog) return;
	posthog.reset();
}

/** The current PostHog replay/session id, or null when the SDK isn't initialised
 *  (no key / pre-boot / SSR). Used to stamp a flagged chat with its replay id so
 *  the admin investigation queue can deep-link straight to the recording. PostHog
 *  exposes this as `get_session_id()`; we guard the call so a SDK-version change
 *  that drops it degrades to null rather than throwing. */
export function currentReplaySessionId(): string | null {
	if (!posthog || typeof posthog.get_session_id !== 'function') return null;
	try {
		return posthog.get_session_id() || null;
	} catch {
		return null;
	}
}

/** Build a PostHog session-replay deep-link for a captured replay id, or null when
 *  the project id isn't configured (PUBLIC_POSTHOG_PROJECT_ID empty) or the id is
 *  blank. The replay UI lives on the APP host (e.g. eu.posthog.com), not the
 *  ingestion host in PUBLIC_POSTHOG_HOST (eu.i.posthog.com) — so we strip the `i.`
 *  ingest label to get there. Pure + side-effect-free (no SDK dependency) so it's
 *  callable from any component, even before/without SDK init. */
export function posthogReplayUrl(replaySessionId: string | null | undefined): string | null {
	if (!replaySessionId || !PUBLIC_POSTHOG_PROJECT_ID) return null;
	const appHost = PUBLIC_POSTHOG_HOST.replace('//i.posthog.com', '//posthog.com').replace(
		'.i.posthog.com',
		'.posthog.com',
	);
	return `${appHost.replace(/\/$/, '')}/project/${PUBLIC_POSTHOG_PROJECT_ID}/replay/${encodeURIComponent(replaySessionId)}`;
}

/** Manual pageview — called from +layout.svelte's afterNavigate. PostHog's
 *  built-in pageview listener is disabled (see capture_pageview above). */
export function capturePageview(url?: string): void {
	if (!posthog) return;
	posthog.capture('$pageview', url ? { $current_url: url } : undefined);
}

/** Escape hatch for custom domain events (e.g. "session.created",
 *  "tool.permission.denied"). Autocapture handles clicks/inputs/forms so most
 *  product analytics needs nothing more — reach for this only when a domain
 *  action isn't visible from the DOM. */
export function captureEvent(name: string, properties?: Record<string, unknown>): void {
	if (!posthog) return;
	posthog.capture(name, properties);
}

/** Send a caught error to PostHog error tracking as a `$exception`.
 *
 *  PostHog's implicit exception autocapture (via `defaults` above) only sees
 *  errors that reach `window.onerror` / `unhandledrejection`. Errors that a
 *  framework boundary swallows first — most importantly SvelteKit's
 *  `handleError`, which intercepts component/$effect/load crashes BEFORE the
 *  window handlers — never reach it. Call this from those boundaries (and from
 *  any deliberate catch worth tracking) so the exception lands in error
 *  tracking with a stack instead of dying in a console.error.
 *
 *  No-ops until the SDK has initialised (posthog stays null when
 *  PUBLIC_POSTHOG_KEY is empty, or before initPostHog()'s lazy import resolves
 *  — so very-early-boot errors may still only hit the console). */
export function captureException(error: unknown, extra?: Record<string, unknown>): void {
	if (!posthog) return;
	posthog.captureException(error, extra);
}
